import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Paper,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FlightTakeoff as FlightIcon,
  Add as AddIcon,
  Delete as RemoveIcon,
  LocalShipping as ShippingIcon,
  Timeline as TrackingIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

const LoadingList = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Vuelos y filtros
  const [vuelos, setVuelos] = useState([]);
  const [vueloFilter, setVueloFilter] = useState({
    search: '',
    fechaDesde: '',
    fechaHasta: '',
  });
  
  // Vuelo seleccionado
  const [selectedFlight, setSelectedFlight] = useState(searchParams.get('flight') || '');
  const [vueloData, setVueloData] = useState(null);

  // Loading list
  const [loadingItems, setLoadingItems] = useState([]);
  const [totals, setTotals] = useState({ items: 0, pieces: 0, weight: 0, available_capacity: 0 });

  // Inventario disponible
  const [inventory, setInventory] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [inventoryFilter, setInventoryFilter] = useState('');

  // Asignación parcial
  const [showPartialDialog, setShowPartialDialog] = useState(false);
  const [partialItem, setPartialItem] = useState(null);
  const [partialPieces, setPartialPieces] = useState('');
  const [partialWeight, setPartialWeight] = useState('');

  // Filtrado de vuelos
  const filteredVuelos = useMemo(() => {
    return vuelos.filter(v => {
      // Filtro por búsqueda (Master AWB o destino)
      if (vueloFilter.search) {
        const search = vueloFilter.search.toLowerCase();
        const matchMaster = (v.awb || '').toLowerCase().includes(search);
        const matchDestino = (v.destino || '').toLowerCase().includes(search);
        const matchEquipo = (v.equipo_nombre || '').toLowerCase().includes(search);
        if (!matchMaster && !matchDestino && !matchEquipo) return false;
      }
      
      // Filtro por fecha desde
      if (vueloFilter.fechaDesde && v.fecha) {
        if (v.fecha < vueloFilter.fechaDesde) return false;
      }
      
      // Filtro por fecha hasta
      if (vueloFilter.fechaHasta && v.fecha) {
        if (v.fecha > vueloFilter.fechaHasta) return false;
      }
      
      return true;
    });
  }, [vuelos, vueloFilter]);

  const handleClearFilters = () => {
    setVueloFilter({ search: '', fechaDesde: '', fechaHasta: '' });
  };

  useEffect(() => {
    fetchVuelos();
  }, []);

  useEffect(() => {
    if (selectedFlight) {
      fetchLoadingList();
    }
  }, [selectedFlight]);

  const fetchVuelos = async () => {
    try {
      const res = await api.get(`${endpoints.vuelos.list}?limit=100`);
      const vuelosAbiertos = (res.data.data.vuelos || []).filter(v => !v.cerrado);
      setVuelos(vuelosAbiertos);
    } catch (error) {
      console.error('Error cargando vuelos:', error);
    }
  };

  const fetchLoadingList = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${endpoints.warehouse.loading}?flight=${selectedFlight}`);
      setVueloData(res.data.data.vuelo);
      setLoadingItems(res.data.data.items || []);
      setTotals(res.data.data.totals || { items: 0, pieces: 0, weight: 0, available_capacity: 0 });
    } catch (error) {
      setError(error.response?.data?.message || 'Error al cargar loading list');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      // Obtener TODOS los destinos del vuelo
      const destinos = vueloData?.destino?.split(';').filter(d => d.trim()) || [];
      
      // Si no hay destinos, retornar vacío
      if (destinos.length === 0) {
        setInventory([]);
        return;
      }
      
      // Hacer una petición por cada destino y combinar resultados
      const promises = destinos.map(destino => 
        api.get(`${endpoints.warehouse.list}?available_only=1&destination=${destino.trim()}`)
      );
      
      const responses = await Promise.all(promises);
      
      // Combinar todos los items de todas las respuestas
      const allItems = responses.flatMap(res => res.data.data.items || []);
      
      // Eliminar duplicados por ID (por si acaso)
      const uniqueItems = allItems.filter((item, index, self) => 
        index === self.findIndex(t => t.id === item.id)
      );
      
      setInventory(uniqueItems);
    } catch (error) {
      console.error('Error cargando inventario:', error);
      setInventory([]);
    }
  };

  const handleOpenAdd = () => {
    fetchInventory();
    setShowAddDialog(true);
    setInventoryFilter('');
  };

  const handleAddToLoading = async (item, pieces = null, weight = null) => {
    // Validación: DEBE tener AWB
    if (!item.awb) {
      setError(`El item ${item.reference} no tiene AWB asignado. Agregue un AWB desde el Inventario antes de asignarlo.`);
      return;
    }

    const piecesToAssign = pieces || item.pieces_remaining || item.pieces_received;
    const weightToAssign = weight || item.weight_remaining || item.weight_received;

    try {
      setLoading(true);
      await api.post(endpoints.warehouse.loading, {
        flight_awb: selectedFlight,
        reception_id: item.id,
        pieces_assigned: piecesToAssign,
        weight_assigned: weightToAssign,
      });

      setSuccess(`${item.reference} asignado al vuelo`);
      setShowAddDialog(false);
      setShowPartialDialog(false);
      fetchLoadingList();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al asignar');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPartial = (item) => {
    setPartialItem(item);
    setPartialPieces(item.pieces_remaining || item.pieces_received);
    setPartialWeight(item.weight_remaining || item.weight_received);
    setShowPartialDialog(true);
  };

  const handlePartialAssign = () => {
    if (!partialPieces || partialPieces <= 0) {
      setError('Ingrese piezas válidas');
      return;
    }
    handleAddToLoading(partialItem, parseInt(partialPieces), parseFloat(partialWeight));
  };

  const handleRemoveFromLoading = async (loadingId) => {
    if (!confirm('¿Remover este item de la loading list?')) return;

    try {
      setLoading(true);
      await api.delete(endpoints.warehouse.loading, { data: { id: loadingId } });
      setSuccess('Item removido de la loading list');
      fetchLoadingList();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al remover');
    } finally {
      setLoading(false);
    }
  };

  const getCapacityPercentage = () => {
    if (!vueloData?.maxload) return 0;
    return ((totals.weight || 0) / vueloData.maxload) * 100;
  };

  // Filtrar inventario: SOLO items con AWB y que coincidan con el filtro de búsqueda
  const filteredInventory = inventory.filter(item => {
    // Primero: DEBE tener AWB
    if (!item.awb) return false;
    
    // Luego: aplicar filtro de búsqueda si existe
    if (!inventoryFilter) return true;
    
    return item.reference.toLowerCase().includes(inventoryFilter.toLowerCase()) ||
           item.awb.toLowerCase().includes(inventoryFilter.toLowerCase());
  });

  // Contar items sin AWB para mostrar advertencia
  const itemsSinAwb = inventory.filter(item => !item.awb).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Loading List
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Asignación de carga a vuelos
          </Typography>
        </Box>
        <Button component={Link} to="/aerolinea" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabla de Vuelos con Filtros */}
      {!selectedFlight && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Seleccionar Vuelo
            </Typography>
            
            {/* Filtros */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Buscar"
                  placeholder="Master AWB, destino, equipo..."
                  value={vueloFilter.search}
                  onChange={(e) => setVueloFilter(prev => ({ ...prev, search: e.target.value }))}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Fecha Desde"
                  type="date"
                  value={vueloFilter.fechaDesde}
                  onChange={(e) => setVueloFilter(prev => ({ ...prev, fechaDesde: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Fecha Hasta"
                  type="date"
                  value={vueloFilter.fechaHasta}
                  onChange={(e) => setVueloFilter(prev => ({ ...prev, fechaHasta: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  fullWidth
                  startIcon={<ClearIcon />}
                  sx={{ height: '100%' }}
                >
                  Limpiar
                </Button>
              </Grid>
            </Grid>
          </CardContent>

          {/* Tabla de vuelos */}
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 'bold' } }}>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Master AWB</TableCell>
                  <TableCell>Destino</TableCell>
                  <TableCell>Equipo</TableCell>
                  <TableCell align="right">Capacidad (kg)</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVuelos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <FlightIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        {vuelos.length === 0 
                          ? 'No hay vuelos abiertos disponibles'
                          : 'No se encontraron vuelos con los filtros aplicados'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVuelos.map((v) => (
                    <TableRow 
                      key={v.awb} 
                      hover 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => setSelectedFlight(v.awb)}
                    >
                      <TableCell>
                        <Typography fontWeight="medium">
                          {v.fecha ? new Date(v.fecha + 'T00:00:00').toLocaleDateString('es', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          }) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FlightIcon fontSize="small" color="primary" />
                          <Typography fontWeight="bold">{v.awb}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(v.destino || '').split(';').map((d, i) => (
                            <Chip key={i} label={d} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{v.equipo_nombre}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {parseFloat(v.maxload || 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFlight(v.awb);
                          }}
                        >
                          Seleccionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {filteredVuelos.length > 0 && (
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">
                Mostrando {filteredVuelos.length} de {vuelos.length} vuelos abiertos
              </Typography>
            </Box>
          )}
        </Card>
      )}

      {/* Vuelo seleccionado - Header con botón volver a lista */}
      {selectedFlight && !vueloData && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Info del Vuelo y Capacidad */}
      {vueloData && (
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FlightIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">{vueloData.awb}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {vueloData.fecha ? new Date(vueloData.fecha + 'T00:00:00').toLocaleDateString('es', { 
                          weekday: 'long',
                          day: '2-digit', 
                          month: 'long', 
                          year: 'numeric' 
                        }) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedFlight('');
                        setVueloData(null);
                        setLoadingItems([]);
                      }}
                      startIcon={<ClearIcon />}
                    >
                      Cambiar Vuelo
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAdd}
                    >
                      Agregar Carga
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Destino</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {(vueloData.destino || '').split(';').map((d, i) => (
                    <Chip key={i} label={d} size="small" color="primary" />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Equipo</Typography>
                <Typography variant="h6">{vueloData.equipo}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Master AWB</Typography>
                <Typography variant="h6">{vueloData.master_awb}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totals.items}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Items</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totals.pieces}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Piezas</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {parseFloat(totals.weight || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Kg Asignados</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {parseFloat(totals.available_capacity || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Kg Disponibles</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">Capacidad:</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(getCapacityPercentage(), 100)}
                    sx={{ flexGrow: 1, height: 10, borderRadius: 1 }}
                    color={getCapacityPercentage() > 90 ? 'error' : getCapacityPercentage() > 70 ? 'warning' : 'success'}
                  />
                  <Typography variant="body2" fontWeight="bold">
                    {getCapacityPercentage().toFixed(1)}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Loading List */}
      {selectedFlight && (
        <Card>
          <CardContent sx={{ pb: 0 }}>
            <Typography variant="h6" fontWeight="bold">
              Loading List - {loadingItems.length} item(s)
            </Typography>
          </CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Referencia</TableCell>
                  <TableCell>Destino</TableCell>
                  <TableCell align="right">Piezas</TableCell>
                  <TableCell align="right">Peso (kg)</TableCell>
                  <TableCell>Naturaleza</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Asignado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box>
                        <Typography fontWeight="bold">{item.reference}</Typography>
                        {item.cargo_awb && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              AWB: {item.cargo_awb}
                            </Typography>
                            <Tooltip title="Ver Tracking">
                              <IconButton 
                                size="small" 
                                component={Link}
                                to={`/warehouse/tracking?awb=${item.cargo_awb}`}
                                color="primary"
                                sx={{ p: 0.25 }}
                              >
                                <TrackingIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.destination} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{item.pieces_assigned}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {parseFloat(item.weight_assigned).toLocaleString()}
                    </TableCell>
                    <TableCell>{item.nature || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.status === 'planned' ? 'Planificado' : 
                               item.status === 'staged' ? 'Staging' :
                               item.status === 'loaded' ? 'Cargado' : item.status}
                        size="small"
                        color={item.status === 'loaded' ? 'success' : 
                               item.status === 'staged' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(item.assigned_at).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {item.status === 'planned' && (
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFromLoading(item.id)}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {loadingItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <ShippingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        No hay carga asignada a este vuelo
                      </Typography>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAdd}
                        sx={{ mt: 2 }}
                      >
                        Agregar Carga
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Dialog Agregar Carga */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Agregar Carga al Vuelo {selectedFlight}
          {vueloData && (
            <Typography variant="body2" color="text.secondary">
              Destino: {vueloData.destino} | Disponible: {parseFloat(totals.available_capacity || 0).toLocaleString()} kg
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Buscar"
            value={inventoryFilter}
            onChange={(e) => setInventoryFilter(e.target.value)}
            fullWidth
            size="small"
            sx={{ my: 2 }}
            placeholder="Buscar por referencia o AWB..."
          />

          {itemsSinAwb > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{itemsSinAwb} item(s)</strong> no tienen AWB asignado y no se muestran aquí. 
              Agregue el AWB desde el <strong>Inventario</strong> antes de poder asignarlos a un vuelo.
            </Alert>
          )}

          {filteredInventory.length === 0 ? (
            <Alert severity="info">
              {inventory.length === 0 
                ? 'No hay carga disponible para los destinos de este vuelo'
                : 'No hay carga con AWB disponible. Agregue el AWB a los items desde el Inventario.'}
            </Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Referencia</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell align="right">Piezas</TableCell>
                    <TableCell align="right">Peso (kg)</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInventory.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box>
                          <Typography fontWeight="bold">{item.reference}</Typography>
                          {item.awb && (
                            <Typography variant="caption" color="text.secondary">
                              AWB: {item.awb}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.destination} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {item.pieces_remaining || item.pieces_received}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {parseFloat(item.weight_remaining || item.weight_received).toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleAddToLoading(item)}
                          disabled={loading}
                        >
                          Agregar Todo
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenPartial(item)}
                          disabled={loading}
                          sx={{ ml: 1 }}
                        >
                          Parcial
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Asignación Parcial */}
      <Dialog open={showPartialDialog} onClose={() => setShowPartialDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Asignación Parcial</DialogTitle>
        <DialogContent>
          {partialItem && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Referencia: <strong>{partialItem.reference}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Disponible: {partialItem.pieces_remaining || partialItem.pieces_received} piezas, {' '}
                {parseFloat(partialItem.weight_remaining || partialItem.weight_received).toLocaleString()} kg
              </Typography>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Piezas a asignar"
                type="number"
                value={partialPieces}
                onChange={(e) => setPartialPieces(e.target.value)}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Peso (kg)"
                type="number"
                value={partialWeight}
                onChange={(e) => setPartialWeight(e.target.value)}
                fullWidth
                inputProps={{ min: 0.1, step: 0.1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPartialDialog(false)}>Cancelar</Button>
          <Button onClick={handlePartialAssign} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Asignar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoadingList;

